import React, { useEffect, useRef, useState } from "react";
import "./TeamsComparison.css";
import Graph from "../../../MeDasboard/Graph";
import ActivityChart from "../../../Home/ActivityChart";
import GoalCompletionRate from "./GoalCompletionRate/GoalCompletionRate";
import { useLocation } from "react-router-dom";
import openNotification from "../../../utils/CustomNotification";
import { toApiDate1 } from "../../../utils/DateUtils";
import AgeGroupBiasedChart from "./AgeGroupBiasedChart/AgeGroupBiasedChart"
import { ApexOptions } from "apexcharts";
import TeamPerformanceComparison from "./TeamPerformanceComparison/TeamPerformanceComparison"
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";

type Series = {
  name: string;
  data: number[];
};
interface GCRMetric {
  [key: string]: any;
}

interface GCRDataEntry {
  date: string;
  metrics: GCRMetric[];
}

interface GCRTeam {
  group_name: string;
  data: GCRDataEntry[];
}

const API_URL = process.env.REACT_APP_API_URL;

const TeamsComparison: React.FC = () => {
  useCheckSessionHeartBeat();
  const location = useLocation();
  const selectedTeams = (location.state?.selectedTeams ?? []) as any[];
  const [selectedMetricAge, setSelectedMetricAge] = useState<"steps" | "distance" | "caloriesburned">("steps");
  const [selectedMetricPerf, setSelectedMetricPerf] = useState<"steps" | "distance" | "caloriesburned">("steps");
  const [selectedMetricGCR, setSelectedMetricGCR] = useState<"steps" | "distance" | "caloriesburned">("steps");

  const [teams, setTeams] = useState<any[]>(selectedTeams);
  const [selectedDate, setSelectedDate] = useState<[Date | null, Date | null]>([
    new Date(new Date().setDate(new Date().getDate() - 6)),
    new Date(),
  ]);

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [dateData, setDateData] = useState<any[]>([]);
  const [goalCompletionData, setGoalCompletionData] = useState<GCRTeam[]>([]);
  const [comparisonData, setComparisonData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);



  useEffect(() => {
    if (dateData.length > 0) {
      processPerformanceChartData(dateData, selectedMetricPerf);
    }
  }, [selectedMetricPerf, dateData]);

  useEffect(() => {
    if (comparisonData?.date_summary_metrics) {
      const processedGCR = processGCRChartData(comparisonData.date_summary_metrics);
      setGoalCompletionData(processedGCR);
    }
  }, [comparisonData]);

  const [ageGroupSeries, setAgeGroupSeries] = useState<{
    steps: Series[];
    distance: Series[];
    caloriesburned: Series[];
  }>({
    steps: [],
    distance: [],
    caloriesburned: [],
  });

  const [ageGroupCategories, setAgeGroupCategories] = useState<string[]>([]);

  const [performanceGraphData, setPerformanceGraphData] = useState<{
    labels: string[];
    series: { name: string; data: number[] }[];
  }>({
    labels: [],
    series: [],
  });

  const [performanceYAxisMax, setPerformanceYAxisMax] = useState<number | undefined>(undefined);

  const goalRef = useRef<HTMLDivElement>(null);
  const biasedRef = useRef<HTMLDivElement>(null);
  const performanceRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const isDateRangeValid = (from: Date, to: Date) => {
    const diff = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
    return diff <= 9;
  };

  const categories = dateData?.[0]?.data.map((entry: { date: string }) =>
    new Date(entry.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
  ) || [];

  const buildChartSeries = (metric: "steps" | "distance" | "caloriesburned") => {
    return dateData.map((team) => ({
      name: team.group_name,
      data: team.data.map((day: { metrics?: any[] }) => {
        const daily = day.metrics?.[0];
        return daily?.[metric]?.value ?? 0;
      }),
    }));
  };
  const activitySeries = buildChartSeries(selectedMetricGCR);

  const processPerformanceChartData = (
    data: any[],
    selectedMetric: "steps" | "distance" | "caloriesburned"
  ) => {
    const dateSet = new Set<string>();

    data.forEach((team: any) => {
      team.data.forEach((entry: any) => {
        dateSet.add(entry.date);
      });
    });

    const sortedDates = Array.from(dateSet).sort();
    const labels = sortedDates.map(dateStr => {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      });
    });

    // Step 2: Build series for each team
    const performanceSeries = data.map((team: any) => {
      const valueMap: Record<string, number> = {};

      team.data.forEach((entry: any) => {
        const metricValue =
          entry.metrics?.[0]?.[selectedMetric]?.value ?? 0;
        valueMap[entry.date] = metricValue;
      });

      const values = sortedDates.map(date => valueMap[date] || 0);

      return {
        name: team.group_name,
        data: values,
      };
    });

    const maxVal = Math.max(...performanceSeries.flatMap(team => team.data));
    const computedMax = Math.ceil((maxVal * 1.1) / 1000) * 1000;

    setPerformanceGraphData({
      labels,
      series: performanceSeries,
    });

    setPerformanceYAxisMax(computedMax || 100); // Default fallback if all values are 0
  };



  const processGCRChartData = (dateSummaryMetrics: any[]) => {
    return dateSummaryMetrics.map((team: any) => ({
      group_name: team.group_name,
      data: team.data.map((entry: any) => ({
        date: entry.date,
        metrics: entry.metrics ?? [],
      })),
    }));
  };

  const fetchComparisonData = async (from: Date, to: Date) => {
    const sessionUid = localStorage.getItem("session_uid") ?? "";
    const userUid = localStorage.getItem("user_uid") ?? "";
    const group_uid_list = selectedTeams.map((t) => t.group_uid);

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        from_datetime: toApiDate1(from),
        to_datetime: toApiDate1(to),
      });

      group_uid_list.forEach((uid) => {
        params.append("group_uid_list", uid);
      });

      const response = await fetch(`${API_URL}/api/get-team-comparison?${params.toString()}`, {
        method: "GET",
        headers: {
          accept: "application/json",
          // "session-uid": sessionUid,
          "user-uid": userUid,
          
        },
        credentials:"include",
      });

      const result = await response.json();

      if (response.ok) {
        setComparisonData(result?.data);
        const summary = result.data?.summary_metrics || [];
        const dateWise = result.data?.date_summary_metrics || [];
        const ageBias = result.data?.age_bias_metrics || [];

        console.log("✅ summaryData from API:", summary);
        setSummaryData(summary);
        setDateData(dateWise);
        processPerformanceChartData(dateWise, selectedMetricPerf);

        // ✅ Process age_bias_metrics for AgeGroupBiasedChart
        const metricMap: {
          steps: Series[];
          distance: Series[];
          caloriesburned: Series[];
        } = {
          steps: [],
          distance: [],
          caloriesburned: [],
        };
        let dates: string[] = [];

        ageBias.forEach((team: any) => {
          const teamName: string = team.group_name || "Unknown";
          const dailyData = team.data || [];

          // Extract dates from first team
          if (!dates.length && dailyData.length > 0) {
            dates = dailyData.map((entry: any) =>
              new Date(entry.date).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
              })
            );
            setAgeGroupCategories(dates);
          }

          // Prepare data points per metric
          ["steps", "distance", "caloriesburned"].forEach((metricKey) => {
            const values = dailyData.map(
              (entry: any) => entry.metrics?.[0]?.[metricKey]?.value ?? 0
            );
            metricMap[metricKey as keyof typeof metricMap].push({
              name: teamName,
              data: values,
            });
          });
        });

        setAgeGroupSeries({
          steps: metricMap.steps,
          distance: metricMap.distance,
          caloriesburned: metricMap.caloriesburned,
        });

      } else {
        openNotification({ type: "error", message: result.message || "Failed to load data." });
      }
      SessionExtend();
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      openNotification({ type: "error", message: "Something went wrong." });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate[0] && selectedDate[1]) {
      const from = selectedDate[0];
      const to = selectedDate[1];

      if (to < from) {
        openNotification({
          type: "error",
          message: "End date cannot be earlier than Start date.",
        });
        return;
      }

      if (!isDateRangeValid(from, to)) {
        openNotification({
          type: "warning",
          message: "Please select a date range of 10 days or fewer.",
        });
        return;
      }

      fetchComparisonData(from, to);
    }
  }, [selectedDate]);

  const handleMetricChange = (metric: string) => {
    if (metric === "steps" || metric === "distance" || metric === "caloriesburned") {
      setSelectedMetricPerf(metric);
    }
  };
  console.log(" Loaded summary metrics:", comparisonData?.summary_metrics);
  return (
    <div className="teams-comparison-container">
      <div className="teams-comparison-heading">
        <h3>Teams Comparison </h3>
        <p>Visualize performance, goal completion, and key metrics across selected teams</p>
      </div>
      {/* DATE RANGE PICKER */}
      <div className="TeamsComp-date-range-container">
        <div className="date-picker">
          <label>
            START DATE
            <input
              type="date"
               value={
                selectedDate?.[0] &&
                  !isNaN(new Date(selectedDate[0]).getTime())
                  ? new Date(selectedDate[0]).toISOString().split("T")[0]
                  : ""
              }
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => {
                // Handle empty value (when cleared)
                if (!e.target.value || e.target.value === '') {
                  setSelectedDate([null, selectedDate[1]]);
                  return;
                }

                // Handle valid date input
                const newDate = new Date(e.target.value);
                if (!isNaN(newDate.getTime())) {
                  setSelectedDate([newDate, selectedDate[1]]);
                }
              }}
            />
          </label>
        </div>
        <div className="date-picker">
          <label>
            END DATE
            <input
              type="date"
              value={
                selectedDate?.[1] &&
                  !isNaN(new Date(selectedDate[1]).getTime())
                  ? new Date(selectedDate[1]).toISOString().split("T")[0]
                  : ""
              }
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => {
                // Handle empty value (when cleared)
                if (!e.target.value || e.target.value === '') {
                  setSelectedDate([selectedDate[0], null]);
                  return;
                }

                // Handle valid date input
                const newDate = new Date(e.target.value);
                if (!isNaN(newDate.getTime())) {
                  setSelectedDate([selectedDate[0], newDate]);
                }
              }}
            />
          </label>
        </div>
      </div>

      {/* LOADING INDICATOR */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '20px',
          fontSize: '14px',
          color: '#666'
        }}>
          Loading comparison data...
        </div>
      )}

      {/* TEAM METRICS */}
      {!isLoading && <div className="team-metrics-wrapper comparison-columns">
        {comparisonData?.summary_metrics?.map((team: any, index: number) => {
          const metrics = team.data?.[0]?.metrics?.[0] ?? {};
          const stepsPercent = metrics.steps?.performance ?? 0;
          const distancePercent = metrics.distance?.performance ?? 0;
          const caloriesPercent = metrics.caloriesburned?.performance ?? 0;
          const performance = team.data?.[0]?.overall_performance ?? 0;
          const gcr = team.data?.[0]?.overall_gcr ?? 0;

          return (
            <div key={index} className="comparison-team-column">
              <h4>{team.group_name}</h4>
              <p style={{ fontSize: "13px", color: "#777" }}>
                Team Members: {team.user_count}
              </p>

              {/* Steps */}
              <div className="bar-label">Steps</div>
              <div className="value-label">{stepsPercent}%</div>
              <div className="bar-row">
                <div
                  className="bar-fill"
                  style={{
                    width: `${Number(stepsPercent)}%`,
                    backgroundColor: "#094752",
                  }}
                />
              </div>

              {/* Distance */}
              <div className="bar-label">Distance</div>
              <div className="value-label">{distancePercent}%</div>
              <div className="bar-row">
                <div
                  className="bar-fill"
                  style={{
                    width: `${Number(distancePercent)}%`,
                    backgroundColor: "#3BAEA0",
                  }}
                />
              </div>

              {/* Calories */}
              <div className="bar-label">Calories</div>
              <div className="value-label">{caloriesPercent}%</div>
              <div className="bar-row">
                <div
                  className="bar-fill"
                  style={{
                    width: `${Number(caloriesPercent)}%`,
                    backgroundColor: "#F25F5C",
                  }}
                />
              </div>

              {/* Performance */}
              <div className="bar-label"> Overall Performance</div>
              <div className="value-label">{performance}%</div>
              <div className="bar-row">
                <div
                  className="bar-fill"
                  style={{
                    width: `${performance}%`,
                    backgroundColor: "#7E57C2",
                  }}
                />
              </div>

              {/* GCR */}
              <div className="bar-label">Goal Completion Rate</div>
              <div className="value-label">{gcr}%</div>
              <div className="bar-row">
                <div
                  className="bar-fill"
                  style={{
                    width: `${gcr}%`,
                    backgroundColor: "#FFB300",
                  }}
                />
              </div>
            </div>
          );
        })}


      </div>}
      {/* TOGGLE BUTTONS */}
      {/* <div className="comparison-toggle-buttons">
        <button onClick={() => scrollToSection(biasedRef)}>Age Group Biased Chart</button>
        <button onClick={() => scrollToSection(performanceRef)}>Performance Chart</button>
        <button onClick={() => scrollToSection(goalRef)}>Goal Completion Rate</button>
      </div> */}
      {!isLoading && <>
      <div className="TeamsCompGraphHeading">
        <h3>Comparision Graphs</h3>
      </div>

      {/* GRAPHS */}
      <div ref={biasedRef} className="chart-section">
        <h5>Age Group Biased Chart</h5>
        <AgeGroupBiasedChart
          categories={ageGroupCategories}
          selectedMetric={selectedMetricAge}
          series={ageGroupSeries[selectedMetricAge]}
          onMetricChange={setSelectedMetricAge}
        />
      </div>

      <div ref={performanceRef} className="chart-section">
        <TeamPerformanceComparison
          selectedMetric={selectedMetricPerf}
          onMetricChange={handleMetricChange}
          categories={performanceGraphData.labels}
          series={performanceGraphData.series}
        />

      </div>

      <GoalCompletionRate data={goalCompletionData} />
      </>}
    </div>
  );
};
export default TeamsComparison;